import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Icon } from "semantic-ui-react";
import { useLoginCheck } from "../../../../hooks/useLoginCheck";
import "../../../../styles/scss/recital2027SpecialWork.scss";
import "../../../../styles/scss/recital2027MediaSubmission.scss";

/**
 * 発表会2027 インストラクターの素材提出状況（音源・照明プラン・場当たり動画）。
 *
 * インストラクターは slash_website の /recital2027/instructor/media から担当ナンバー
 * （演目）ごとに提出する。この画面は提出状況を一覧で見るだけ（書き込みなし）。
 * ファイル名を押すと Cloudflare 経由で開く。
 *
 * API: GET /api/get_recital2027_media_overview/（views_recital_media.py）
 * 集計カード・表・チップは特別ナンバー画面（recital2027SpecialWork.scss）を流用する。
 */

const apiBase = process.env.REACT_APP_API_URL;

const STATUS_FILTERS = [
  { value: "all", label: "すべて" },
  { value: "missing", label: "未提出あり" },
  { value: "none", label: "未着手" },
  { value: "complete", label: "すべて提出済み" },
];

// 列ごとの並び替えの値。null（初期状態）はサーバの並び（インストラクター側と同じ順）。
// 素材の列は提出件数で並べるので、昇順にすると未提出のナンバーが先頭に来る。
const SORT_VALUES = {
  number: (w) => w._order,
  instructor: (w) => w.instructors.map((i) => i.name).join(" / ").toLowerCase(),
  music: (w) => (w.files.music || []).length,
  lighting_plan: (w) => (w.files.lighting_plan || []).length,
  rehearsal_video: (w) => (w.files.rehearsal_video || []).length,
  last: (w) => w.last_uploaded_at || "",
};

const fmtSize = (bytes) => {
  if (!bytes && bytes !== 0) return "";
  if (bytes >= 1024 ** 3) return `${(bytes / 1024 ** 3).toFixed(1)}GB`;
  if (bytes >= 1024 ** 2) return `${(bytes / 1024 ** 2).toFixed(1)}MB`;
  return `${Math.max(1, Math.round(bytes / 1024))}KB`;
};

function MediaSubmissionPage() {
  useLoginCheck();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [query, setQuery] = useState("");
  const [kind, setKind] = useState("all");
  const [status, setStatus] = useState("all");
  const [instructorId, setInstructorId] = useState("");
  const [sort, setSort] = useState({ key: null, dir: "asc" });

  const fetchOverview = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${apiBase}/api/get_recital2027_media_overview/`, {
        credentials: "include",
      });
      if (!res.ok) throw new Error(`取得に失敗しました (${res.status})`);
      setData(await res.json());
      setError("");
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchOverview();
  }, [fetchOverview]);

  // _order: サーバが返した並び（ナンバー列の並び替えと、同じ値のときの順番に使う）
  const works = useMemo(() => (data?.works || []).map((w, i) => ({ ...w, _order: i })), [data]);
  const assetTypes = data?.asset_types || [];
  const summary = data?.summary;

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return works.filter((w) => {
      if (kind !== "all" && w.kind !== kind) return false;
      if (instructorId && !w.instructors.some((i) => String(i.id) === instructorId)) return false;
      const done = Object.values(w.submitted);
      if (status === "missing" && w.complete) return false;
      if (status === "none" && done.some(Boolean)) return false;
      if (status === "complete" && !w.complete) return false;
      if (q) {
        const hay = [w.title, ...w.lessons, ...w.instructors.map((i) => i.name)].join(" ").toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [works, query, kind, status, instructorId]);

  const sorted = useMemo(() => {
    const value = SORT_VALUES[sort.key];
    if (!value) return filtered;
    const sign = sort.dir === "asc" ? 1 : -1;
    return [...filtered].sort((a, b) => {
      const va = value(a);
      const vb = value(b);
      const c = typeof va === "number" ? va - vb : String(va).localeCompare(String(vb), "ja");
      return c !== 0 ? c * sign : a._order - b._order;
    });
  }, [filtered, sort]);

  // 同じ列をもう一度押すと昇順 ⇄ 降順を切り替える
  const toggleSort = (key) =>
    setSort((s) => (s.key === key ? { key, dir: s.dir === "asc" ? "desc" : "asc" } : { key, dir: "asc" }));

  const SortHead = ({ k, children }) => (
    <span
      role="columnheader"
      aria-sort={sort.key === k ? (sort.dir === "asc" ? "ascending" : "descending") : "none"}
    >
      <button type="button" onClick={() => toggleSort(k)}>
        {children}
        <span className="arrow">{sort.key === k ? (sort.dir === "asc" ? "↑" : "↓") : "↕"}</span>
      </button>
    </span>
  );

  const kindCounts = useMemo(() => {
    const c = {};
    works.forEach((w) => {
      c[w.kind] = (c[w.kind] || 0) + 1;
    });
    return c;
  }, [works]);

  return (
    <div className="recital2027-special-page recital2027-media-page">
      <div className="page-header">
        <div>
          <p className="eyebrow">SLASH GALLERY vol.5</p>
          <h1>音源の提出状況</h1>
          <p className="header-sub">
            インストラクターが提出した音源・照明プラン・場当たり動画をナンバーごとに確認します（提出はインストラクター用ページから）。
          </p>
        </div>
        <button type="button" className="ghost-btn" onClick={fetchOverview}>
          <Icon name="refresh" /> 再読み込み
        </button>
      </div>

      {error && <div className="alert">{error}</div>}

      {loading && !data ? (
        <div className="loading">読み込み中…</div>
      ) : summary ? (
        <>
          <div className="kpis">
            <div className="kpi">
              <span className="k">3種類すべて提出済み</span>
              <span className="v">
                {summary.complete}
                <small>/ {summary.works} ナンバー</small>
              </span>
            </div>
            {assetTypes.map((t) => (
              <div className="kpi" key={t.key}>
                <span className="k">{t.label}</span>
                <span className="v">
                  {summary.by_type[t.key]}
                  <small>/ {summary.works}</small>
                </span>
                <span className="sub">未提出 {summary.works - summary.by_type[t.key]}</span>
              </div>
            ))}
            <div className={`kpi${summary.none ? " warn" : ""}`}>
              <span className="k">未着手</span>
              <span className="v">
                {summary.none}
                <small>ナンバー</small>
              </span>
              <span className="sub">1つも提出されていない</span>
            </div>
          </div>

          <section>
            <div className="toolbar">
              <div className="search-bar">
                <Icon name="search" />
                <input
                  type="text"
                  placeholder="ナンバー名・講師名・クラス名で検索"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                />
              </div>
              <select className="sel" value={instructorId} onChange={(e) => setInstructorId(e.target.value)}>
                <option value="">すべての講師</option>
                {(data.instructors || []).map((i) => (
                  <option key={i.id} value={String(i.id)}>
                    {i.name}（{i.complete}/{i.works}）
                  </option>
                ))}
              </select>
              <div className="chipset">
                <button type="button" className="chip" aria-pressed={kind === "all"} onClick={() => setKind("all")}>
                  すべて
                </button>
                {(data.kinds || [])
                  .filter((k) => kindCounts[k.key])
                  .map((k) => (
                    <button
                      key={k.key}
                      type="button"
                      className="chip"
                      aria-pressed={kind === k.key}
                      onClick={() => setKind(k.key)}
                    >
                      {k.label}
                    </button>
                  ))}
              </div>
              <div className="chipset">
                {STATUS_FILTERS.map((f) => (
                  <button
                    key={f.value}
                    type="button"
                    className="chip"
                    aria-pressed={status === f.value}
                    onClick={() => setStatus(f.value)}
                  >
                    {f.label}
                  </button>
                ))}
              </div>
              <span className="spacer" />
              <span className="dim">{filtered.length} 件</span>
            </div>

            <div className="table-wrap">
              <div className="scroller">
                <div className="grid media-grid">
                  <div className="row head">
                    <SortHead k="number">ナンバー</SortHead>
                    <SortHead k="instructor">担当</SortHead>
                    {assetTypes.map((t) => (
                      <SortHead key={t.key} k={t.key}>
                        {t.label}
                      </SortHead>
                    ))}
                    <SortHead k="last">最終提出</SortHead>
                  </div>
                  {sorted.length === 0 ? (
                    <div className="empty">該当するナンバーはありません</div>
                  ) : (
                    sorted.map((w) => (
                      <div className="row body static" key={w.id}>
                        <span className="name">{w.title}</span>
                        <span>{w.instructors.map((i) => i.name).join(" / ") || <span className="dash">—</span>}</span>
                        {assetTypes.map((t) => {
                          const files = w.files[t.key] || [];
                          return (
                            <span key={t.key} className="media-files">
                              {files.length === 0 ? (
                                <span className="pill missing">未提出</span>
                              ) : (
                                files.map((f) => (
                                  <a
                                    key={f.id}
                                    href={f.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="media-file"
                                    title={`${f.name}\nアップロード時の名前: ${f.original_name}\n${fmtSize(f.size_bytes)}・${f.uploaded_at}・${f.uploaded_by}`}
                                  >
                                    <span className="media-file-name">{f.name}</span>
                                  </a>
                                ))
                              )}
                            </span>
                          );
                        })}
                        <span className="mono media-date">{w.last_uploaded_at || <span className="dash">—</span>}</span>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
          </section>
        </>
      ) : null}
    </div>
  );
}

export default MediaSubmissionPage;
